// JournalArticleForm migration: 2. XML export — insert reference citations into generated XML
import { CROSSREF_NS } from '../xml-helpers';

/**
 * After the MetadataConverterAPI returns journal article XML, adds a
 * <citation_list> under <journal_article> from the form references field.
 *
 * The API response does not include references, so we patch them in
 * client-side before deposit. Each reference is one free-text line rendered as
 * <unstructured_citation>.
 */
export function insertCitationsToXML(
  depositXml: string,
  references: string[] | null | undefined
): string {
  const doc = parseDepositXml(depositXml);

  if (alreadyHasCitationList(doc)) {
    return depositXml;
  }

  const referenceLines = normalizeReferenceLines(references);
  if (referenceLines.length === 0) {
    return depositXml;
  }

  const journalArticle = findJournalArticleElement(doc);
  appendCitationList(journalArticle, referenceLines);

  return serializeDepositXml(doc);
}

/** Parse the XML string returned by the convert API. */
function parseDepositXml(xml: string): Document {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');

  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    throw new Error(
      `Invalid XML input: ${parseError.textContent?.trim() || 'Unknown error'}`
    );
  }

  return doc;
}

/** If citations were already included, do not add a second list. */
function alreadyHasCitationList(doc: Document): boolean {
  if (doc.querySelector('citation_list')) {
    console.debug(
      'insertCitationsToXML: citation_list already present; leaving XML unchanged.'
    );
    return true;
  }
  return false;
}

/** Trim empty lines; return [] when there is nothing to add. */
function normalizeReferenceLines(
  references: string[] | null | undefined
): string[] {
  if (!Array.isArray(references)) {
    console.warn(
      'insertCitationsToXML: references is not an array; leaving XML unchanged.'
    );
    return [];
  }

  const lines = references
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) {
    console.warn(
      'insertCitationsToXML: no non-empty reference lines; leaving XML unchanged.'
    );
  }

  return lines;
}

/** Citations must live inside <journal_article> per Crossref deposit shape. */
function findJournalArticleElement(doc: Document): Element {
  const journalArticle = doc.querySelector('journal_article');
  if (!journalArticle) {
    throw new Error('<journal_article> not found in deposit XML.');
  }
  return journalArticle;
}

/**
 * Build:
 *   <citation_list>
 *     <citation key="ref0"><unstructured_citation>...</unstructured_citation></citation>
 *     ...
 *   </citation_list>
 */
function appendCitationList(parent: Element, referenceLines: string[]): void {
  const doc = parent.ownerDocument!;
  const citationList = doc.createElementNS(CROSSREF_NS, 'citation_list');

  referenceLines.forEach((text, index) => {
    const citation = doc.createElementNS(CROSSREF_NS, 'citation');
    citation.setAttribute('key', `ref${index}`);

    const unstructuredCitation = doc.createElementNS(
      CROSSREF_NS,
      'unstructured_citation'
    );
    unstructuredCitation.textContent = text;

    citation.appendChild(unstructuredCitation);
    citationList.appendChild(citation);
  });

  parent.appendChild(citationList);
}

function serializeDepositXml(doc: Document): string {
  return new XMLSerializer().serializeToString(doc);
}
